import type { ProductWithDetails } from '../types';
import { DELIVERY_TIMES } from '../constants';
import { useLaboratoryStore } from '../stores';
import { useConfigStore } from '../stores/configStore';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export function validateRow(row: any): ValidationResult {
  const errors: string[] = [];
  const laboratoryStore = useLaboratoryStore();

  if (!row || typeof row !== 'object') {
    errors.push('Fila inválida: debe ser un objeto con las propiedades requeridas');
    return { isValid: false, errors };
  }

  // Validar Nombre
  if (!row.Nombre || typeof row.Nombre !== 'string' || row.Nombre.trim() === '') {
    errors.push('El campo Nombre es requerido y debe ser texto no vacío');
  }

  // Validar PrecioDeCosto
  const precio = Number(row.PrecioDeCosto);
  if (isNaN(precio)) {
    errors.push('Precio de costo inválido. Debe ser un número');
  } else if (precio === 0) {
    return { isValid: false, errors: ['Precio cero - producto salteado'] };
  } else if (precio < 0) {
    errors.push('El precio de costo no puede ser negativo');
  }

  // Validar Factor
  const factor = Number(row.Factor);
  if (!row.Factor || isNaN(factor) || factor <= 0) {
    errors.push('Factor inválido. Debe ser un número mayor que 0');
  }

  // Validar Laboratorio
  if (!row.Laboratorio || typeof row.Laboratorio !== 'string' || row.Laboratorio.trim() === '') {
    errors.push('Laboratorio no especificado');
  } else {
    const labExists = laboratoryStore.laboratories.some(lab => 
      lab.Nombre.toLowerCase() === row.Laboratorio.toLowerCase()
    );
    if (!labExists) {
      errors.push(
        "Laboratorio '" + row.Laboratorio + "' no existe. " +
        "Disponibles: " + laboratoryStore.laboratories.map(l => l.Nombre).join(', ')
      );
    }
  }

  // Validar TiempoDeDemora
  if (!row.TiempoDeDemora || !DELIVERY_TIMES.includes(row.TiempoDeDemora)) {
    errors.push(
      "Tiempo de demora '" + (row.TiempoDeDemora || '') + "' no válido. " +
      "Valores permitidos: " + DELIVERY_TIMES.join(', ')
    );
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

export function processExcelData(
  jsonData: any[],
  categoryId: string
): {
  products: ProductWithDetails[];
  skipped: Array<{ name: string; reason: string }>;
  invalid: Record<number, string[]>;
} {
  const laboratoryStore = useLaboratoryStore();
  const configStore = useConfigStore();
  const productMap = new Map<string, ProductWithDetails>();
  const skippedProducts: Array<{ name: string; reason: string }> = [];
  const invalidRows: Record<number, string[]> = {};

  // Obtener configuración de la categoría
  const categoryConfig = configStore.getCategoryConfig(categoryId);

  jsonData.forEach((row, index) => {
    const validation = validateRow(row);
    
    if (!validation.isValid) {
      if (validation.errors[0] === 'Precio cero - producto salteado') {
        skippedProducts.push({
          name: row.Nombre,
          reason: 'Precio de costo igual a 0'
        });
      } else {
        invalidRows[index] = validation.errors;
      }
      return;
    }

    const laboratory = laboratoryStore.laboratories.find(lab => 
      lab.Nombre.toLowerCase() === row.Laboratorio.toLowerCase()
    );
    if (!laboratory) return;

    if (!productMap.has(row.Nombre)) {
      // Usar el campo Rango como Notas si la categoría está configurada para notas
      const rangoValue = row.Rango || '';
      
      productMap.set(row.Nombre, {
        Nombre: row.Nombre,
        Rango: categoryConfig.hasNotes ? rangoValue : '',
        Factor: Number(row.Factor),
        Categoria: categoryId,
        laboratories: []
      });
    }

    const product = productMap.get(row.Nombre)!;
    product.laboratories.push({
      laboratory,
      details: {
        productId: '',
        laboratoryId: laboratory.id!,
        PrecioDeCosto: Number(row.PrecioDeCosto),
        TiempoDeDemora: row.TiempoDeDemora,
        Disponible: row.Disponible ?? true,
        UltimaActualizacion: new Date()
      }
    });
  });

  return {
    products: Array.from(productMap.values()),
    skipped: skippedProducts,
    invalid: invalidRows
  };
}